import { useSyncExternalStore } from 'react';
import { getEnvConfig } from './config';

/** Named product feature gates (school allowlist + optional feature-flag override). */
export const FeatureGateName = {
  annotationsEnabled: 'annotationsEnabled',
  webinarEnabled: 'webinarEnabled',
  customThemeEnabled: 'customThemeEnabled',
  // Demo features: Manage Courses, Learning Homework, all-type content upload.
  manageCoursesEnabled: 'manageCoursesEnabled',
  learningHomeworkEnabled: 'learningHomeworkEnabled',
  allContentUploadsEnabled: 'allContentUploadsEnabled',
} as const;

export type FeatureGateName =
  (typeof FeatureGateName)[keyof typeof FeatureGateName];

export type FeatureGateOptions = {
  /** School to evaluate. Falls back to the synced school id when omitted. */
  schoolId?: number | null;
  /**
   * Optional feature-flag value (e.g. from school feature plan / remote config).
   * When provided as a boolean, both the school allowlist and this flag must be true.
   */
  featureFlag?: boolean | null;
};

let featureSchoolId: number | undefined;
const listeners = new Set<() => void>();

/** Keep shared-ui feature gates in sync with the active school (set from each app). */
export function setFeatureSchoolId(id?: number | null) {
  const next = typeof id === 'number' && Number.isFinite(id) ? id : undefined;
  if (next === featureSchoolId) return;
  featureSchoolId = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSyncedSchoolId() {
  return featureSchoolId;
}

export function getFeatureAllowedSchoolIds() {
  const { environment } = getEnvConfig();
  if (environment === 'dev') return [8];
  if (environment === 'prod') return [904, 1];
  return [904];
}

export function getFeatureAllowedSchoolId() {
  return getFeatureAllowedSchoolIds()[0];
}

export function isFeatureSchoolAllowed(schoolId?: number | null) {
  return (
    typeof schoolId === 'number' &&
    getFeatureAllowedSchoolIds().includes(schoolId)
  );
}

/**
 * Evaluate a named feature gate.
 * Base rule: school id must be allowlisted (8 on dev, 904 and 1 on prod, 904 elsewhere).
 * Optional `featureFlag`: when a boolean is passed, it is ANDed with the school rule.
 *
 * @example isFeatureGateEnabled('webinarEnabled', { schoolId: 8 })
 * @example isFeatureGateEnabled('customThemeEnabled', { schoolId, featureFlag: planFlag })
 */
export function isFeatureGateEnabled(
  _featureName: FeatureGateName,
  options: FeatureGateOptions = {}
): boolean {
  const schoolId =
    options.schoolId !== undefined ? options.schoolId : getSyncedSchoolId();
  const schoolAllowed = isFeatureSchoolAllowed(schoolId ?? null);

  if (typeof options.featureFlag === 'boolean') {
    return schoolAllowed && options.featureFlag;
  }

  return schoolAllowed;
}

/**
 * React hook for named feature gates (uses school id from `setFeatureSchoolId`).
 * @example useFeatureGate('webinarEnabled')
 * @example useFeatureGate('annotationsEnabled', { featureFlag: planFlag })
 * @example useFeatureGate('customThemeEnabled', { featureFlag: true })
 */
export function useFeatureGate(
  featureName: FeatureGateName,
  options: FeatureGateOptions = {}
): boolean {
  const syncedSchoolId = useSyncExternalStore(
    subscribe,
    getSyncedSchoolId,
    () => undefined
  );

  const schoolId =
    options.schoolId !== undefined ? options.schoolId : syncedSchoolId;

  return isFeatureGateEnabled(featureName, {
    ...options,
    schoolId,
  });
}

/** Convenience wrappers — prefer `useFeatureGate('<name>', …)` for new call sites. */
export function useAnnotationsEnabled(featureFlag?: boolean | null) {
  return useFeatureGate(FeatureGateName.annotationsEnabled, { featureFlag });
}

export function useWebinarEnabled(featureFlag?: boolean | null) {
  return useFeatureGate(FeatureGateName.webinarEnabled, { featureFlag });
}

export function useCustomThemeEnabled(featureFlag?: boolean | null) {
  return useFeatureGate(FeatureGateName.customThemeEnabled, { featureFlag });
}
